import resolveModule from "@app/resolveModule/frontend";
import Path from "@core/FileProvider/Path/Path";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreator from "@core-ui/ContextServices/ApiUrlCreator";
import { LfsPointerError, type ResourceError } from "@core-ui/ContextServices/ResourceService/errors";
import WorkspaceService from "@core-ui/ContextServices/Workspace";
import downloadResource from "@core-ui/downloadResource";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import FileTranscription from "@ext/ai/components/Audio/FileTranscription";
import { useLegacyAiAvailable } from "@ext/ai/components/useLegacyAiAvailable";
import { ALLOWED_MEDIA_EXTENSIONS as ALLOWED_MEDIA_EXTENSIONS_AI } from "@ext/ai/models/consts";
import t from "@ext/localization/locale/translate";
import {
	resolveToolbarContext,
	type ToolbarContext,
} from "@ext/markdown/elements/article/edit/helpers/resolveToolbarContext";
import type { Editor } from "@tiptap/core";
import { GlassToolbarButton, GlassToolbarIcon, GlassToolbarSeparator } from "@ui-kit/GlassToolbar";
import { toast } from "@ui-kit/Toast";
import { TooltipProvider } from "@ui-kit/Tooltip";
import Name from "./Menu/Name";

interface FileMenuProps {
	editor: Editor;
	context: Extract<ToolbarContext, { kind: "file" }>;
}

const FileMenu = ({ editor, context }: FileMenuProps) => {
	const { resourcePath } = context;
	const isLegacyAiAvailable = useLegacyAiAvailable();
	const { isTauri, isWeb, isStatic } = usePlatform();
	const workspace = WorkspaceService.current();
	const path = new Path(window.decodeURIComponent(resourcePath));
	const isMediaFile = isLegacyAiAvailable && ALLOWED_MEDIA_EXTENSIONS_AI.includes(path.extension.toLowerCase());
	const apiUrlCreator = ApiUrlCreator.value;

	const onDelete = () => {
		if (!editor || editor.isDestroyed) return;
		const current = resolveToolbarContext(editor.state);
		if (current.kind !== "file" || current.from !== context.from || current.resourcePath !== resourcePath) return;
		editor.view.dispatch(editor.state.tr.removeMark(current.from, current.to, editor.schema.marks.file));
		editor.commands.focus();
	};

	const onError = (err: ResourceError | null) => {
		if (err instanceof LfsPointerError) {
			toast(t("file-not-found"), {
				status: "warning",
				icon: "cloud-alert",
				description: t("git.lfs.file-is-pointer-2"),
				size: "lg",
			});
			return;
		}

		toast(t("file-not-found"), {
			status: "error",
			icon: "triangle-alert",
			description: t("file-download-error-message"),
			size: "lg",
		});
	};

	const download = async () => {
		await downloadResource(apiUrlCreator, path);
	};

	const openInSupportedApp = async () => {
		const res = await FetchService.fetch(apiUrlCreator.getResourcePath(path.value));
		if (!res.ok) return onError(null);

		const absolutePath = await res.text();
		try {
			await resolveModule("openInExplorer")?.(new Path(workspace.path).join(new Path(absolutePath)).value);
		} catch (error) {
			onError(error === "lfs-pointer" ? new LfsPointerError(path.value) : null);
			console.error(error);
		}
	};

	return (
		<TooltipProvider>
			<Name downloadResource={download} onError={onError} openInSupportedApp={openInSupportedApp} path={path} />
			<GlassToolbarSeparator variant="inline" />
			{isMediaFile && <FileTranscription path={path} />}
			{(isWeb || isStatic) && (
				<GlassToolbarButton onClick={download} tooltipText={t("download")}>
					<GlassToolbarIcon icon="download" />
				</GlassToolbarButton>
			)}
			{isTauri && (
				<GlassToolbarButton onClick={openInSupportedApp} tooltipText={t("open-in-supported-app")}>
					<GlassToolbarIcon icon="external-link" />
				</GlassToolbarButton>
			)}
			<GlassToolbarButton onClick={onDelete} tooltipText={t("delete-file")}>
				<GlassToolbarIcon icon="trash" />
			</GlassToolbarButton>
		</TooltipProvider>
	);
};

export default FileMenu;
